'use client';

import { c as _c } from "react/compiler-runtime";
import type { GetStudentMentoringStateOutputDTO } from '@algoacademy/backend/modules/payment/endpoints/queries/get-student-mentoring-state/output.dto';
import { LoadingSpinner } from '@algoacademy/design-system/web/atoms/LoadingSpinner';
import { ErrorMessage, Field, Fieldset, Label } from '@algoacademy/design-system/web/forms/Fieldset';
import { Input } from '@algoacademy/design-system/web/forms/Input';
import { H4 } from '@algoacademy/design-system/web/typography/Heading';
import { P3 } from '@algoacademy/design-system/web/typography/Paragraph';
import type { UseFormReturn } from 'react-hook-form';
import type { MentoringOfferFormSchema } from '../../schema';
import { NoActiveSubscriptionNotice } from './NoActiveSubscriptionNotice';
import { StudentSubscriptionPanel } from './StudentSubscriptionPanel';
type StudentLookupSectionProps = {
  form: UseFormReturn<MentoringOfferFormSchema>;
  isChangeMode: boolean;
  isBlocked: boolean;
  isStudentEmailValid: boolean;
  isLookupPending: boolean;
  mentoringState: GetStudentMentoringStateOutputDTO | undefined;
  current: NonNullable<GetStudentMentoringStateOutputDTO['current']> | null;
};
export function StudentLookupSection(t0) {
  const $ = _c(38);
  const {
    form,
    isChangeMode,
    isBlocked,
    isStudentEmailValid,
    isLookupPending,
    mentoringState,
    current
  } = t0;
  const showIdleHint = !isStudentEmailValid;
  const showLookupStatus = isStudentEmailValid;
  const showNoActiveSubscription = showLookupStatus && !isLookupPending && mentoringState !== undefined && mentoringState.subscriptionState === "none" && current === null;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <H4>Dane ucznia</H4>;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== current || $[2] !== form || $[3] !== isBlocked || $[4] !== isChangeMode) {
    t2 = !isBlocked && <Field className="flex-1"><Label>Imię i nazwisko</Label>{isChangeMode && current !== null ? <Input type="text" value={current.studentName} readOnly={true} aria-readonly="true" /> : <><Input {...form.register("studentName")} type="text" autoComplete="off" /><ErrorMessage>{form.formState.errors.studentName?.message}</ErrorMessage></>}</Field>;
    $[1] = current;
    $[2] = form;
    $[3] = isBlocked;
    $[4] = isChangeMode;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <Label>Adres e-mail</Label>;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== form) {
    t4 = form.register("studentEmail");
    $[7] = form;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== t4) {
    t5 = <Input {...t4} type="email" autoComplete="off" />;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const t6 = form.formState.errors.studentEmail?.message;
  let t7;
  if ($[11] !== t6) {
    t7 = <ErrorMessage>{t6}</ErrorMessage>;
    $[11] = t6;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== t5 || $[14] !== t7) {
    t8 = <Field className="flex-1">{t3}{t5}{t7}</Field>;
    $[13] = t5;
    $[14] = t7;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== t2 || $[17] !== t8) {
    t9 = <Fieldset className="flex flex-col gap-3 lg:flex-row">{t2}{t8}</Fieldset>;
    $[16] = t2;
    $[17] = t8;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== showIdleHint) {
    t10 = showIdleHint ? <P3 className="text-contentTertiary">Po wpisaniu poprawnego adresu e-mail sprawdzamy, czy uczeń ma już subskrypcję.</P3> : null;
    $[19] = showIdleHint;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] !== isLookupPending || $[22] !== showLookupStatus) {
    t11 = showLookupStatus && isLookupPending ? <div className="flex items-center gap-2"><LoadingSpinner size="button" className="text-contentSecondary" /><P3 role="status" className="text-contentSecondary">Sprawdzamy subskrypcję ucznia...</P3></div> : null;
    $[21] = isLookupPending;
    $[22] = showLookupStatus;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  let t12;
  if ($[24] !== mentoringState || $[25] !== showNoActiveSubscription) {
    t12 = showNoActiveSubscription ? <NoActiveSubscriptionNotice unpaidOffer={mentoringState.unpaidOffer} /> : null;
    $[24] = mentoringState;
    $[25] = showNoActiveSubscription;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  let t13;
  if ($[27] !== current || $[28] !== isLookupPending || $[29] !== mentoringState || $[30] !== showLookupStatus) {
    t13 = showLookupStatus && !isLookupPending && current !== null && mentoringState !== undefined ? <StudentSubscriptionPanel subscriptionState={mentoringState.subscriptionState} current={current} scheduledChange={mentoringState.scheduledChange} /> : null;
    $[27] = current;
    $[28] = isLookupPending;
    $[29] = mentoringState;
    $[30] = showLookupStatus;
    $[31] = t13;
  } else {
    t13 = $[31];
  }
  let t14;
  if ($[32] !== t10 || $[33] !== t11 || $[34] !== t12 || $[35] !== t13 || $[36] !== t9) {
    t14 = <section className="space-y-3">{t1}{t9}{t10}{t11}{t12}{t13}</section>;
    $[32] = t10;
    $[33] = t11;
    $[34] = t12;
    $[35] = t13;
    $[36] = t9;
    $[37] = t14;
  } else {
    t14 = $[37];
  }
  return t14;
}